import { Controller, FormProvider, useForm } from 'react-hook-form';
import FieldRow from 'components/FormElements/FieldRow';
import TextField from 'components/FormElements/AppTextInput';
import AppPasswordInput from 'components/FormElements/AppPasswordTextField';
import { Box, CircularProgress } from '@mui/material';
import Dropzone, { UploadedFile } from 'components/Dropzone';
import Spacing from 'components/Spacing';
import FieldSwitch from 'components/FormElements/FieldSwitch';
import PageSection from 'components/PageSection';
import Heading from 'components/Heading';
import { FIRM } from 'adminConstants/routes';
import { zodResolver } from '@hookform/resolvers/zod';
import useFileUpload from 'hooks/ui/useFileUpload';
import { useParams, useHistory, Link } from 'react-router-dom';
import { useNotification } from 'hooks/ui/useNotification';
import { eisFirm as rules } from 'further-ui/validations';
import Button from 'components/Button';
import ButtonGroup from 'components/ButtonGroup';
import { Grid } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';

import api from 'lib/trpcClient';
import { createFirmEditRoute } from 'adminConstants/routes';
import { FileType } from 'further-ui/utils';
import { UploadType } from 'further-types/files';
import { z } from 'zod';
import { RolePermission } from 'further-types/admin-user-role';
import { useHasPermissions } from 'hooks/ui/usePermissions';

type FormValues = z.infer<typeof rules.CreateEisFirmInput>;

const GeneralInformation: React.FC = () => {
  const { uploadFile } = useFileUpload();
  const { id } = useParams<{ id?: string }>();
  const { error, success } = useNotification();
  const queryClient = useQueryClient();
  const history = useHistory();
  const [canEditFirm] = useHasPermissions([RolePermission.EditFirm]);

  const isCreatePage = !id;

  const apiUtils = api.useUtils();

  const { mutate: createEisFirm, isPending: isCreating } =
    api.eisFirm.create.useMutation({
      onSuccess: (data) => {
        success('Firm created successfully.');
        queryClient.invalidateQueries({ queryKey: ['firms'] });
        history.push(createFirmEditRoute(data.id));
      },
      onError: (err) => {
        error(err?.message ?? 'Something went wrong.');
      },
    });

  const { mutate: updateEisFirm, isPending: isUpdating } =
    api.eisFirm.updateGeneralInformation.useMutation({
      onSuccess: () => {
        success('Firm updated successfully.');
        apiUtils.eisFirm.getById.invalidate({ firmId: id ?? '' });
      },
      onError: (err) => {
        error(err?.message ?? 'Something went wrong.');
      },
    });

  const { data: eisFirm, isLoading } = api.eisFirm.getById.useQuery(
    { firmId: id ?? '' },
    { enabled: !!id },
  );

  const handleFileUpload = async (
    files: Array<UploadedFile>,
    onSuccess: (
      uploadFileResults: Array<{
        filePath: string;
        fileName: string;
        preview: string;
      }>,
    ) => void,
  ) => {
    if (!files?.[0]?.path) return;
    const filesToUpload: Array<{
      filePath: string;
      fileName: string;
      preview: string;
    }> = [];
    let hadError = false;
    for await (const file of files) {
      const { data, error: uploadError } = await uploadFile(
        file,
        UploadType.FirmDocuments,
      );
      if (data) {
        filesToUpload.push({
          filePath: data?.filePath,
          fileName: file.name || '',
          preview: data?.url,
        });
      } else {
        error(uploadError.message);
        hadError = true;
        break;
      }
    }

    if (!hadError) onSuccess(filesToUpload);
  };

  const formMethods = useForm<FormValues>({
    shouldFocusError: true,
    resolver: zodResolver(rules.CreateEisFirmInput),
    values: {
      firmName: eisFirm?.firmName ?? '',
      keyContactName: eisFirm?.keyContactName ?? '',
      keyContactEmail: eisFirm?.keyContactEmail ?? '',
      secondaryContactName: eisFirm?.secondaryContactName ?? '',
      secondaryContactEmail: eisFirm?.secondaryContactEmail ?? '',
      summaryAttachments: eisFirm?.summaryAttachments ?? [],
      systemEmailRecipient: eisFirm?.systemEmailRecipient ?? '',
      excelFilesPassword: eisFirm?.excelFilesPassword ?? '',
      excelFilesPasswordExists: eisFirm?.excelFilesPasswordExists ?? false,
      newInvestmentWebhookUrl:
        eisFirm?.webhookSettings?.newInvestmentWebhookUrl ?? '',
      investorDataWebhookUrl:
        eisFirm?.webhookSettings?.investorDataWebhookUrl ?? '',
      investmentEditsWebhookUrl:
        eisFirm?.webhookSettings?.investmentEditsWebhookUrl ?? '',
      adviserDataWebhookUrl:
        eisFirm?.webhookSettings?.adviserDataWebhookUrl ?? '',
    },
    criteriaMode: 'all',
    reValidateMode: 'onSubmit',
  });

  const { handleSubmit, control, setValue, watch } = formMethods;
  const onSubmit = async (data) => {
    if (id) {
      await updateEisFirm({ ...data, firmId: id });
    } else {
      await createEisFirm(data);
    }
  };

  const excelFilesPasswordExists = watch('excelFilesPasswordExists');

  return (
    <>
      <FormProvider {...formMethods}>
        {isLoading ? (
          <CircularProgress />
        ) : (
          <form>
            <Spacing size="lg">
              <PageSection title="General information">
                <Spacing>
                  <FieldRow title="Firm name*" centerTitle>
                    <Controller
                      name="firmName"
                      disabled={!canEditFirm}
                      control={control}
                      render={({
                        field: { ref, ...rest },
                        fieldState: { error },
                      }) => (
                        <TextField
                          {...rest}
                          inputRef={ref}
                          placeholder="e.g. Acme Ventures"
                          fullWidth
                          helperText={error?.message}
                          error={!!error}
                        />
                      )}
                    />
                  </FieldRow>

                  <FieldRow title="Key contact name*" centerTitle>
                    <Controller
                      name="keyContactName"
                      disabled={!canEditFirm}
                      control={control}
                      render={({
                        field: { ref, ...rest },
                        fieldState: { error },
                      }) => (
                        <TextField
                          {...rest}
                          inputRef={ref}
                          placeholder="Who is your firm's main user of Further?"
                          fullWidth
                          helperText={error?.message}
                          error={!!error}
                        />
                      )}
                    />
                  </FieldRow>

                  <FieldRow title="Key contact email address*" centerTitle>
                    <Controller
                      name="keyContactEmail"
                      disabled={!canEditFirm}
                      control={control}
                      render={({
                        field: { ref, ...rest },
                        fieldState: { error },
                      }) => (
                        <TextField
                          {...rest}
                          inputRef={ref}
                          placeholder="Please enter their email address"
                          fullWidth
                          helperText={error?.message}
                          error={!!error}
                        />
                      )}
                    />
                  </FieldRow>

                  <FieldRow title="Secondary contact name" centerTitle>
                    <Controller
                      name="secondaryContactName"
                      disabled={!canEditFirm}
                      control={control}
                      render={({
                        field: { ref, ...rest },
                        fieldState: { error },
                      }) => (
                        <TextField
                          {...rest}
                          inputRef={ref}
                          placeholder="Who is your firm's secondary user of Further?"
                          fullWidth
                          helperText={error?.message}
                          error={!!error}
                        />
                      )}
                    />
                  </FieldRow>

                  <FieldRow title="Secondary contact email address" centerTitle>
                    <Controller
                      name="secondaryContactEmail"
                      disabled={!canEditFirm}
                      control={control}
                      render={({
                        field: { ref, ...rest },
                        fieldState: { error },
                      }) => (
                        <TextField
                          {...rest}
                          inputRef={ref}
                          placeholder="Please enter their email address"
                          fullWidth
                          helperText={error?.message}
                          error={!!error}
                        />
                      )}
                    />
                  </FieldRow>

                  <FieldRow
                    title="Summary email attachment (PDF only)"
                    informationText="Any file added here will be attached to your daily subscription summary emails. This attachment option can be used for daily AML sign off letters or other required confirmation files"
                  >
                    <Controller
                      name="summaryAttachments"
                      control={control}
                      disabled={!canEditFirm}
                      render={({
                        field: { value, name, onChange },
                        fieldState: { error },
                      }) => (
                        <>
                          <Dropzone
                            name={name}
                            disabled={!canEditFirm}
                            accept={{
                              [FileType.PDF]: ['.pdf'],
                            }}
                            maxFiles={10}
                            onDropFile={async (files) => {
                              await handleFileUpload(
                                files,
                                (uploadFileResults) => {
                                  return onChange([
                                    ...(value || []),
                                    ...uploadFileResults,
                                  ]);
                                },
                              );
                            }}
                            files={(value || []).map((file) => ({
                              preview: file?.preview ?? file?.filePath ?? '',
                              fileName: file?.fileName ?? '',
                              path: file?.filePath ?? '',
                            }))}
                            onRemove={(deleteIndex) => {
                              setValue(
                                name,
                                (value || []).filter(
                                  (_, index) => index !== deleteIndex,
                                ),
                              );
                            }}
                          />
                          {error && <p>{error.message}</p>}
                        </>
                      )}
                    />
                  </FieldRow>

                  <FieldRow
                    title="Subscription flow email recipient(s)"
                    informationText="Separate emails by a semi-colon"
                  >
                    <Controller
                      name="systemEmailRecipient"
                      disabled={!canEditFirm}
                      render={({
                        field: { ref, ...rest },
                        fieldState: { error },
                      }) => (
                        <>
                          <TextField
                            {...rest}
                            inputRef={ref}
                            placeholder="Enter the email addresses you wish to be blind-copied into client subscription flow emails. These include registration, 24 hour cool off and subscription receipt emails sent to investors. Due to a potential large volume of emails, we recommend personal email addresses are not used. Please separate each email with a semi-colon. e.g. 'a@a.com;b@b.com'"
                            fullWidth
                            multiline
                            minRows={4}
                            error={!!error}
                            helperText={error?.message}
                          />
                        </>
                      )}
                    />
                  </FieldRow>

                  <FieldRow title="Password protect spreadsheets sent in emails?">
                    <Controller
                      name="excelFilesPasswordExists"
                      render={({ field: { value } }) => (
                        <Box display="flex" gap={16} flexDirection="column">
                          <FieldSwitch
                            disabled={!canEditFirm}
                            checked={!!value}
                            checkedLabel="Yes"
                            uncheckedLabel="No"
                            isControlled={false}
                            onChange={(event) => {
                              setValue(
                                'excelFilesPasswordExists',
                                event.target.checked,
                              );
                              if (!event.target.checked) {
                                setValue('excelFilesPassword', '');
                              }
                            }}
                          />
                        </Box>
                      )}
                    />
                  </FieldRow>
                  {excelFilesPasswordExists ? (
                    <FieldRow title="Spreadsheet password">
                      <Controller
                        name="excelFilesPassword"
                        disabled={!canEditFirm}
                        render={({
                          field: { ref, value, ...rest },
                          fieldState: { error },
                        }) => (
                          <AppPasswordInput
                            {...rest}
                            inputRef={ref}
                            value={value}
                            fullWidth
                            error={!!error}
                            helperText={error?.message}
                          />
                        )}
                      />
                    </FieldRow>
                  ) : null}
                </Spacing>
              </PageSection>

              <PageSection
                showDivider
                headerContent={
                  <Heading
                    variant="h2"
                    subtitle="Connect Further to external services, such as your CRM, using a webhook. You can make use of these through Zapier or a similar service."
                  >
                    Webhook settings
                  </Heading>
                }
              >
                <Spacing size="sm">
                  <FieldRow
                    title="New subscription webhook URL"
                    centerTitle
                    tooltipText="This webhook triggers on a subscription successfully being made. It posts all investor and subscription information."
                  >
                    <Controller
                      name="newInvestmentWebhookUrl"
                      control={control}
                      disabled={!canEditFirm}
                      render={({
                        field: { ref, ...rest },
                        fieldState: { error },
                      }) => (
                        <TextField
                          {...rest}
                          inputRef={ref}
                          placeholder="Enter the URL for the webhook"
                          fullWidth
                          error={!!error}
                          helperText={error?.message}
                        />
                      )}
                    />
                  </FieldRow>
                  <FieldRow
                    title="Subscription edits webhook URL"
                    centerTitle
                    tooltipText="This webhook triggers when an existing subscription's total commitment or commitment tag is changed. It posts all subscription information."
                  >
                    <Controller
                      name="investmentEditsWebhookUrl"
                      control={control}
                      disabled={!canEditFirm}
                      render={({
                        field: { ref, ...rest },
                        fieldState: { error },
                      }) => (
                        <TextField
                          {...rest}
                          inputRef={ref}
                          placeholder="Enter the URL for the webhook"
                          fullWidth
                          error={!!error}
                          helperText={error?.message}
                        />
                      )}
                    />
                  </FieldRow>
                  <FieldRow
                    title="Investor data webhook URL"
                    centerTitle
                    tooltipText="This webhook triggers on investor account creation, details changing, KYC completion and 24-hour cool-off completion. It posts all investor account information."
                  >
                    <Controller
                      name="investorDataWebhookUrl"
                      control={control}
                      disabled={!canEditFirm}
                      render={({
                        field: { ref, ...rest },
                        fieldState: { error },
                      }) => (
                        <TextField
                          {...rest}
                          inputRef={ref}
                          placeholder="Enter the URL for the webhook"
                          fullWidth
                          error={!!error}
                          helperText={error?.message}
                        />
                      )}
                    />
                  </FieldRow>
                  <FieldRow
                    title="Adviser data webhook URL"
                    centerTitle
                    tooltipText="This webhook triggers on adviser account creation or details changing. It posts all adviser account information."
                  >
                    <Controller
                      name="adviserDataWebhookUrl"
                      control={control}
                      disabled={!canEditFirm}
                      render={({
                        field: { ref, ...rest },
                        fieldState: { error },
                      }) => (
                        <TextField
                          {...rest}
                          inputRef={ref}
                          placeholder="Enter the URL for the webhook"
                          fullWidth
                          error={!!error}
                          helperText={error?.message}
                        />
                      )}
                    />
                  </FieldRow>
                </Spacing>
              </PageSection>

              <Grid size={{ xs: 4 }}>
                <ButtonGroup>
                  <Link to={FIRM}>
                    <Button variant="outlined">Cancel</Button>
                  </Link>
                  {canEditFirm ? (
                    <Button
                      variant="contained"
                      color="primary"
                      type="button"
                      onClick={handleSubmit(onSubmit)}
                      loading={isCreating || isUpdating}
                    >
                      {isCreatePage ? 'Create firm' : 'Save'}
                    </Button>
                  ) : null}
                </ButtonGroup>
              </Grid>
            </Spacing>
          </form>
        )}
      </FormProvider>
    </>
  );
};

export default GeneralInformation;
